/**
 * ContentForge — Approvals View
 *
 * HITL review queue with category tabs and approve/reject actions.
 */

import { html, nothing } from "lit";

// ─── Types ──────────────────────────────────────────────────────────────

export type CfApprovalCategory = "outline" | "products" | "draft" | "repair";
export type CfApprovalStatus = "pending" | "approved" | "rejected";

export type CfApproval = {
  id: string;
  category: CfApprovalCategory;
  keywordId: string | null;
  articleId: string | null;
  title: string;
  agentType: string;
  status: CfApprovalStatus;
  note: string | null;
  data: Record<string, unknown>;
  createdAt: number;
  resolvedAt: number | null;
};

export type CfApprovalsProps = {
  approvals: CfApproval[];
  loading: boolean;
  error: string | null;
  activeCategory: CfApprovalCategory | "all";
  previewHtml: string | null;
  onRefresh: () => void;
  onResolve: (id: string, decision: "approved" | "rejected", note?: string) => void;
  onCategoryChange: (category: CfApprovalCategory | "all") => void;
  onPreviewArticle?: (articleId: string) => void;
  onPreviewOutline?: (data: Record<string, unknown>) => void;
  onRegenerateImage?: (articleId: string, customPrompt?: string) => void;
  onClosePreview?: () => void;
};

// ─── Constants ──────────────────────────────────────────────────────────

const CATEGORIES: { key: CfApprovalCategory | "all"; label: string; accent: string }[] = [
  { key: "all", label: "All", accent: "var(--cf-accent-blue, #60a5fa)" },
  { key: "outline", label: "Outlines", accent: "var(--cf-accent-indigo, #818cf8)" },
  { key: "products", label: "Products", accent: "var(--cf-accent-purple, #a78bfa)" },
  { key: "draft", label: "Drafts", accent: "var(--cf-accent-yellow, #fbbf24)" },
  { key: "repair", label: "Repairs", accent: "var(--cf-accent-orange, #fb923c)" },
];

const AGENT_LABELS: Record<string, string> = {
  strategist: "Strategist",
  sourcer: "Sourcer",
  writer: "Writer",
  artist: "Artist",
  publisher: "Publisher",
  auditor: "Auditor",
  miner: "Miner",
};

// ─── Helpers ────────────────────────────────────────────────────────────

function formatRelativeTime(timestamp: number): string {
  const diff = Date.now() - timestamp;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) {
    return "just now";
  }
  if (mins < 60) {
    return `${mins}m ago`;
  }
  const hours = Math.floor(mins / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function categoryAccent(cat: CfApprovalCategory): string {
  return CATEGORIES.find((c) => c.key === cat)?.accent ?? "var(--cf-accent-blue, #60a5fa)";
}

// ─── Render ─────────────────────────────────────────────────────────────

export function renderCfApprovals(props: CfApprovalsProps) {
  const {
    approvals,
    loading,
    error,
    activeCategory,
    previewHtml,
    onRefresh,
    onResolve,
    onCategoryChange,
    onPreviewArticle,
    onPreviewOutline,
    onRegenerateImage,
    onClosePreview,
  } = props;

  const filtered = (
    activeCategory === "all" ? approvals : approvals.filter((a) => a.category === activeCategory)
  )
    .slice()
    .toSorted((a, b) => {
      // Pending first, then newest first
      if (a.status === "pending" && b.status !== "pending") {
        return -1;
      }
      if (a.status !== "pending" && b.status === "pending") {
        return 1;
      }
      return b.createdAt - a.createdAt;
    });

  const pending = approvals.filter((a) => a.status === "pending").length;

  return html`
    <div class="cf-approvals">
      <!-- Header -->
      <div class="cf-tasks__header">
        <div class="cf-tasks__title">
          <h2>Approvals</h2>
          ${pending > 0 ? html`<span class="cf-approvals__badge">${pending}</span>` : nothing}
          <button class="cf-btn cf-btn--sm cf-btn--ghost" @click=${onRefresh}>
            Refresh
          </button>
        </div>
        ${error ? html`<div class="cf-error">${error}</div>` : nothing}
      </div>

      <!-- Category Tabs -->
      <div class="cf-approvals__tabs">
        ${CATEGORIES.map((cat) => {
          const count =
            cat.key === "all"
              ? approvals.length
              : approvals.filter((a) => a.category === cat.key).length;
          return html`
            <button
              class="cf-approvals__tab ${activeCategory === cat.key ? "cf-approvals__tab--active" : ""}"
              style="--tab-accent: ${cat.accent}"
              @click=${() => onCategoryChange(cat.key)}
            >
              ${cat.label}
              <span class="cf-approvals__tab-count">${count}</span>
            </button>
          `;
        })}
      </div>

      <!-- Stats -->
      <div class="cf-stats">
        <div class="cf-stats__item">
          <span class="cf-stats__value">${pending}</span>
          <span class="cf-stats__label">Pending</span>
        </div>
        <div class="cf-stats__item">
          <span class="cf-stats__value">${approvals.filter((a) => a.status === "approved").length}</span>
          <span class="cf-stats__label">Approved</span>
        </div>
        <div class="cf-stats__item">
          <span class="cf-stats__value">${approvals.filter((a) => a.status === "rejected").length}</span>
          <span class="cf-stats__label">Rejected</span>
        </div>
      </div>

      <!-- Loading -->
      ${
        loading
          ? html`
              <div class="cf-loading">Loading approvals…</div>
            `
          : nothing
      }

      <!-- Cards -->
      <div class="cf-approvals__list">
        ${
          filtered.length === 0
            ? html`
                <div class="cf-approvals__empty">
                  <div class="cf-approvals__empty-icon">✓</div>
                  <div class="cf-approvals__empty-title">No pending approvals</div>
                  <div class="cf-approvals__empty-desc">
                    Approvals will appear here when agents produce content for review.
                  </div>
                </div>
              `
            : filtered.map((a) =>
                renderApprovalCard(
                  a,
                  onResolve,
                  onPreviewArticle,
                  onPreviewOutline,
                  onRegenerateImage,
                ),
              )
        }
      </div>

      <!-- Preview Modal -->
      ${
        previewHtml
          ? html`
          <div class="cf-preview-overlay" @click=${onClosePreview}>
            <div class="cf-preview-modal" @click=${(e: Event) => e.stopPropagation()}>
              <div class="cf-preview-modal__header">
                <span class="cf-preview-modal__title">📄 Article Preview</span>
                <button class="cf-btn cf-btn--sm cf-btn--ghost" @click=${onClosePreview}>✕ Close</button>
              </div>
              <iframe
                class="cf-preview-modal__iframe"
                .srcdoc=${previewHtml}
                sandbox="allow-scripts"
              ></iframe>
            </div>
          </div>
        `
          : nothing
      }
    </div>
  `;
}

// ─── Approval Card ──────────────────────────────────────────────────────

function renderApprovalCard(
  approval: CfApproval,
  onResolve: (id: string, decision: "approved" | "rejected", note?: string) => void,
  onPreviewArticle?: (articleId: string) => void,
  onPreviewOutline?: (data: Record<string, unknown>) => void,
  onRegenerateImage?: (articleId: string, customPrompt?: string) => void,
) {
  const isPending = approval.status === "pending";

  // Render product details for "products" approvals
  const productDetails =
    approval.category === "products" && approval.data?.products
      ? html`
        <div class="cf-approval-card__products">
          ${(
            approval.data.products as Array<{
              productId: string;
              title: string;
              price: number;
              imageUrl?: string;
              affCode?: string;
            }>
          ).map(
            (p) => html`
              <div class="cf-approval-card__product">
                ${
                  p.imageUrl
                    ? html`<img
                      class="cf-approval-card__product-img"
                      src="${p.imageUrl}"
                      alt="${p.title}"
                      loading="lazy"
                      @error=${(e: Event) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />`
                    : nothing
                }
                <div class="cf-approval-card__product-info">
                  <div class="cf-approval-card__product-title">${p.title}</div>
                  <div class="cf-approval-card__product-price">${p.price} RON</div>
                </div>
                ${
                  p.affCode
                    ? html`<a
                      class="cf-approval-card__product-link"
                      href="${p.affCode}"
                      target="_blank"
                      rel="noopener"
                      title="View product"
                    >↗</a>`
                    : nothing
                }
              </div>
            `,
          )}
        </div>
      `
      : nothing;

  // Render outline sections for "outline" approvals
  const outlineDetails =
    approval.category === "outline" && approval.data?.sections
      ? html`
        <div class="cf-approval-card__outline">
          ${(approval.data.sections as Array<{ h2: string }>).map(
            (s) => html`<div class="cf-approval-card__outline-section">📝 ${s.h2}</div>`,
          )}
          ${
            onPreviewOutline
              ? html`
              <button
                class="cf-btn cf-btn--sm cf-btn--ghost cf-preview-btn"
                title="Preview outline"
                @click=${() => onPreviewOutline(approval.data)}
              >
                👁 Preview Outline
              </button>
            `
              : nothing
          }
        </div>
      `
      : nothing;

  // Render draft stats for "draft" approvals
  const draftDetails =
    approval.category === "draft" && approval.data
      ? html`
        <div class="cf-approval-card__draft">
          <div class="cf-approval-card__draft-stats">
            <span class="cf-approval-card__draft-stat">📝 ${approval.data.wordCount ?? 0} words</span>
            <span class="cf-approval-card__draft-stat">📦 ${approval.data.productCount ?? 0} products</span>
            <span class="cf-approval-card__draft-stat">❓ ${approval.data.faqCount ?? 0} FAQs</span>
            <span class="cf-approval-card__draft-stat">🔗 ${approval.data.internalLinkCount ?? 0} internal links</span>
            <span class="cf-approval-card__draft-stat">${approval.data.hasFeaturedImage ? "🖼️ Image ready" : "⚠️ No image"}</span>
          </div>
          ${
            onPreviewArticle && approval.data.articleId
              ? html`
              <button
                class="cf-btn cf-btn--sm cf-btn--ghost cf-preview-btn"
                title="Preview article"
                @click=${() => onPreviewArticle(approval.data.articleId as string)}
              >
                👁 Preview Article
              </button>
            `
              : nothing
          }
          ${
            onRegenerateImage && approval.data.articleId
              ? html`
              <input
                class="cf-input"
                id="cf-regen-prompt-${approval.id}"
                placeholder="Custom image prompt (leave empty for default)"
                style="font-size: 0.8rem; margin-bottom: 4px"
              />
              <button
                class="cf-btn cf-btn--sm cf-btn--ghost cf-preview-btn"
                title="Regenerate featured image"
                @click=${() => {
                  const input = document.getElementById(
                    `cf-regen-prompt-${approval.id}`,
                  ) as HTMLInputElement;
                  const customPrompt = input?.value?.trim() || undefined;
                  onRegenerateImage(approval.data.articleId as string, customPrompt);
                }}
              >
                🔄 Regenerate Image
              </button>
            `
              : nothing
          }
        </div>
      `
      : nothing;

  return html`
    <div class="cf-approval-card ${isPending ? "" : "cf-approval-card--resolved"}">
      <div class="cf-approval-card__header">
        <span
          class="cf-approval-card__category"
          style="background: ${categoryAccent(approval.category)}"
        >
          ${approval.category}
        </span>
        <span class="cf-approval-card__agent">
          ${AGENT_LABELS[approval.agentType] ?? approval.agentType}
        </span>
        <span class="cf-approval-card__time">${formatRelativeTime(approval.createdAt)}</span>
      </div>
      <div class="cf-approval-card__title">${approval.title}</div>
      ${approval.note ? html`<div class="cf-approval-card__note">${approval.note}</div>` : nothing}
      ${productDetails}
      ${outlineDetails}
      ${draftDetails}
      ${
        !isPending
          ? html`
              <div class="cf-approval-card__status cf-approval-card__status--${approval.status}">
                ${approval.status === "approved" ? "✓ Approved" : "✕ Rejected"}
              </div>
            `
          : html`
              <div class="cf-approval-card__actions">
                ${
                  approval.category === "draft"
                    ? html`
                      <textarea
                        class="cf-input cf-textarea"
                        id="cf-reject-note-${approval.id}"
                        rows="2"
                        placeholder="What to change? (e.g. 'Make intro shorter', 'Focus more on product X')"
                        style="width:100%;margin-bottom:8px;font-size:0.8rem"
                      ></textarea>
                    `
                    : nothing
                }
                <button
                  class="cf-btn cf-btn--sm cf-btn--success"
                  @click=${() => onResolve(approval.id, "approved")}
                >
                  ✓ Approve
                </button>
                <button
                  class="cf-btn cf-btn--sm cf-btn--danger"
                  @click=${() => {
                    const textarea = document.getElementById(
                      `cf-reject-note-${approval.id}`,
                    ) as HTMLTextAreaElement;
                    const note = textarea?.value?.trim() || undefined;
                    onResolve(approval.id, "rejected", note);
                  }}
                >
                  ✕ Reject
                </button>
              </div>
            `
      }
    </div>
  `;
}
